import { useEffect } from "react";
const MODIFIER_KEYS = [
    "Shift",
    "Control",
    "Alt",
    "Meta"
];
function isModifierKey(key) {
    return MODIFIER_KEYS.includes(key);
}
/**
 * Releases modifier keys that the browser failed to deliver a `keyup` for.
 *
 * macOS screenshot shortcuts (Cmd+Shift+4 / Cmd+Shift+5) intercept Shift
 * before the browser sees its `keyup`, and the screenshot overlay does not
 * blur the window — so neither a `keyup` nor a `blur` reaches the page.
 * ReactFlow keeps Shift "held" in its internal `useKeyPress` state and forces
 * selection-on-drag even in pan mode until something releases the latch.
 *
 * Reconcile on `mousemove`: every `MouseEvent` carries the live modifier
 * state. When the user returns to the canvas, the first mouse movement
 * reveals any phantom-held modifier, and we dispatch a synthetic `keyup` so
 * ReactFlow's window-level listener clears its latch. The mousemove listener
 * is only attached while a modifier is being tracked — normal pan usage pays
 * no per-frame cost.
 */ export function useReleaseStuckModifiers() {
    useEffect(function trackAndReleaseStuckModifiers() {
        const held = new Set();
        let listeningForMouse = false;
        const release = (key)=>{
            held.delete(key);
            window.dispatchEvent(new KeyboardEvent("keyup", {
                key,
                bubbles: true
            }));
        };
        const onMouseMove = (e)=>{
            if (held.has("Shift") && !e.shiftKey) release("Shift");
            if (held.has("Control") && !e.ctrlKey) release("Control");
            if (held.has("Alt") && !e.altKey) release("Alt");
            if (held.has("Meta") && !e.metaKey) release("Meta");
        };
        const syncMouseListener = ()=>{
            if (held.size > 0 && !listeningForMouse) {
                window.addEventListener("mousemove", onMouseMove);
                listeningForMouse = true;
            } else if (held.size === 0 && listeningForMouse) {
                window.removeEventListener("mousemove", onMouseMove);
                listeningForMouse = false;
            }
        };
        const onKeyDown = (e)=>{
            if (!isModifierKey(e.key)) return;
            held.add(e.key);
            syncMouseListener();
        };
        const onKeyUp = (e)=>{
            if (!isModifierKey(e.key)) return;
            held.delete(e.key);
            syncMouseListener();
        };
        window.addEventListener("keydown", onKeyDown);
        window.addEventListener("keyup", onKeyUp);
        return ()=>{
            window.removeEventListener("keydown", onKeyDown);
            window.removeEventListener("keyup", onKeyUp);
            if (listeningForMouse) window.removeEventListener("mousemove", onMouseMove);
        };
    }, []);
}

//# sourceMappingURL=useReleaseStuckModifiers.js.map